import "./Inventory.css";

import { useContext } from "react";

import { CloseLightIcon } from "../../../../../assets";
import { OrganisationTypeConstants, ProjectPermissionConstants } from "../../../../../constants";
import { DeleteIssuanceModal, IssuancePanel } from "../../../../../templates";
import { UnitTransactionsPanel } from "../../../../../templates/UnitTransactionsPanel";
import {
  Button,
  Column,
  CursorDataGrid,
  MultiSelect,
  ProfileMenu,
  ProfileMenuItem,
  Row,
  StackedBarChart,
} from "../../../../../widget";
import { ProjectContext } from "../ProjectContext";
import { ProjectInventoryChartKey } from "./components";
import { MultiIssuanceTransactionPanel } from "./components/MultiIssuanceTransactionPanel";
import { INVENTORY_STATUS_SELECT_DATA, useInventory } from "./useInventory";

const Inventory = (): JSX.Element => {
  const {
    selectedGetProjectIssuancesResponseResults,
    onChange,
    columns,
    isLoading,
    hasData,
    hasEnoughIssuancesForScrolling,
    unitMeasurement,
    xAxisLabels,
    stackedChartData,
    exportFileName,
    currentUserType,
    showIssuancePanel,
    actionsMenuOptionsList,
    exportTable,
    setShowIssuancePanel,
    showEditColumnsPanel,
    setShowEditColumnsPanel,
    showActionsMenu,
    setShowActionsMenu,
    defaultEditColumns,
    getProjectIssuanceResult,
    issuance,
    handleCloseIssuancePanel,
    showUnitTransactionsPanel,
    handleCloseUnitTransactionsPanel,
    issuanceUuid,
    showDeleteIssuanceModal,
    deleteIssuanceRowVersion,
    handleCloseDeleteIssuanceModal,
    refresh,
    handleRefresh,
    inventoryEditColumnsLocalStorageItemName,
    filterExportColumns,
    filterExportRows,
    hasIssuanceSchema,
    selectedRows,
    setSelectedRows,
    showMultiIssuanceTransactionPanel,
    setShowMultiIssuanceTransactionPanel,
    handleCloseMultiIssuanceTransactionPanel,
    tagsFilterSelectData,
    selectedTagKeys,
    onTagsFilter,
    selectedStatuses,
    handleStatusFilter,
  } = useInventory();

  const { hasProjectPermission } = useContext(ProjectContext);

  return (
    <div className="ProjectTabSection">
      <Row spacingV="ll">
        <Column span={12}>
          <div className="ProjectTabHeader">
            <h2>Inventory</h2>
            <div className="ProjectTabHeaderActions">
              {hasProjectPermission(ProjectPermissionConstants.MANAGE_PROJECT_ISSUANCES) && (
                <MultiSelect
                  label=" "
                  placeholder="Issuance status"
                  data={INVENTORY_STATUS_SELECT_DATA}
                  values={selectedStatuses}
                  onChange={handleStatusFilter}
                  className="IssuanceStatusFilter"
                />
              )}
              {hasIssuanceSchema && hasProjectPermission(ProjectPermissionConstants.MANAGE_PROJECT_ISSUANCES) && (
                <Button
                  text="Add issuance"
                  onClick={() => {
                    setShowIssuancePanel(true);
                  }}
                  variant="primary"
                />
              )}
            </div>
          </div>
        </Column>
      </Row>
      {stackedChartData && xAxisLabels && hasData && (
        <Row spacingV="2xl">
          <Column span={12}>
            <StackedBarChart
              xLabels={xAxisLabels}
              data={stackedChartData}
              hasYAxisGridLines
              hasYAxisTicks
              showLegend={false}
            />
            <ProjectInventoryChartKey unitMeasurement={unitMeasurement} />
          </Column>
        </Row>
      )}
      <div className="InventoryTable">
        <div className="InventoryTableButtons">
          {!isLoading && (
            <div className={`Filters ${hasData ? "" : "HasNoData"}`}>
              <h3>Filter by</h3>
              <MultiSelect
                label=" "
                className="TagsFilter"
                placeholder="Tags"
                data={tagsFilterSelectData}
                values={selectedTagKeys}
                onChange={(keys) => onTagsFilter(keys)}
              />
              {selectedTagKeys.length > 0 && (
                <Button
                  text="Clear tags filter"
                  variant="reversedPrimary"
                  textUnderline
                  textBold
                  hasNoPadding
                  onClick={() => onTagsFilter([])}
                />
              )}
            </div>
          )}
          {selectedRows.size > 0 && hasProjectPermission(ProjectPermissionConstants.MANAGE_PROJECT_ISSUANCES) && (
            <>
              <Button
                className="RemoveSelectedButton"
                text={`${selectedRows.size} ${selectedRows.size > 1 ? "vintages" : "vintage"} selected`}
                variant="tertiary"
                icon={<CloseLightIcon />}
                size="small"
                onClick={() => setSelectedRows?.(new Set())}
              />
              <Button
                text={`Add ${selectedRows.size > 1 ? "transactions" : "transaction"}`}
                size="small"
                onClick={() => setShowMultiIssuanceTransactionPanel(true)}
              />
              <div className="Divider" />
            </>
          )}
          {hasData && hasProjectPermission(ProjectPermissionConstants.MANAGE_PROJECT_ISSUANCES) && (
            <div className="Actions">
              <Button text="Table options" variant="tertiary" onClick={() => setShowActionsMenu(true)} size="small" />
              <ProfileMenu show={showActionsMenu} onClose={() => setShowActionsMenu(false)} position={{ top: "0" }}>
                {actionsMenuOptionsList.map((el) => (
                  <ProfileMenuItem
                    key={el.id}
                    profileMenuItem={el}
                    onClick={() => {
                      if (el.action) {
                        el.action();
                      }
                      setShowActionsMenu(false);
                    }}
                  />
                ))}
              </ProfileMenu>
            </div>
          )}
        </div>
        <Row spacingV={hasEnoughIssuancesForScrolling ? "ml" : undefined}>
          <Column span={12}>
            <div className={`ProjectInventoryDataGrid ${hasEnoughIssuancesForScrolling ? "Scrollable" : ""}`}>
              <CursorDataGrid
                columns={columns}
                clientSide={false}
                filterable
                onChange={onChange}
                data={[]}
                dataIsLoading={isLoading}
                noDataMessage="No inventory to display"
                pagination={{ pageSize: 1000 }}
                hasTotalRow
                sortable
                totalColumnNames={["unitQuantityAvailable", "unitQuantityReserved", "unitQuantitySold"]}
                onExternalExport={exportTable}
                exportFileName={exportFileName}
                filterExportColumns={filterExportColumns}
                filterExportRows={filterExportRows}
                refresh={refresh}
                showEditColumnsPanel={showEditColumnsPanel}
                closeEditColumnsPanel={() => setShowEditColumnsPanel(false)}
                editColumnsText="Select the columns to display in the table. Drag headings to rearrange their left-to-right order."
                defaultEditColumns={defaultEditColumns}
                editColumnsLocalStorageItemName={inventoryEditColumnsLocalStorageItemName}
                infiniteScroll
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                variant={currentUserType === OrganisationTypeConstants.ASSET_MANAGER ? "seed" : "default"}
              />
            </div>
          </Column>
        </Row>
      </div>
      <IssuancePanel
        show={showIssuancePanel}
        onClose={handleCloseIssuancePanel}
        issuance={issuance}
        handleRefresh={handleRefresh}
      />
      <DeleteIssuanceModal
        onClose={handleCloseDeleteIssuanceModal}
        show={showDeleteIssuanceModal}
        rowVersion={deleteIssuanceRowVersion}
        issuanceUuid={issuanceUuid}
        handleRefresh={handleRefresh}
      />
      {getProjectIssuanceResult && (
        <UnitTransactionsPanel
          issuance={getProjectIssuanceResult}
          show={showUnitTransactionsPanel}
          onClose={handleCloseUnitTransactionsPanel}
          handleRefresh={handleRefresh}
        />
      )}
      {selectedGetProjectIssuancesResponseResults && selectedGetProjectIssuancesResponseResults?.length > 0 && (
        <MultiIssuanceTransactionPanel
          issuances={selectedGetProjectIssuancesResponseResults}
          show={showMultiIssuanceTransactionPanel}
          onClose={handleCloseMultiIssuanceTransactionPanel}
          handleRefresh={handleRefresh}
        />
      )}
    </div>
  );
};

export default Inventory;
